import { useState, type Ref } from 'react';
import { useParams, Link } from 'react-router';
import { ArrowLeft, Plus, Calendar, Target } from 'lucide-react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { addTask, updateTask, updateSprint, addCommentToEntity, updateCommentInEntity, deleteCommentFromEntity, addReactionToComment } from '../store';
import { Project, Task, TaskStatus, Comment } from '../types';
import TaskCard from '../components/TaskCard';
import CreateTaskModal from '../components/CreateTaskModal';
import TaskDetailsModal from '../components/TaskDetailsModal';
import { CommentSection } from '../components/CommentSection';
import { toast } from 'sonner';
import { useApp } from '../providers/AppProvider';
import { generateId } from '../utils/idGenerator';
import { canCreateProjectTasks, canEditTaskParameters, getProjectTeamMemberIds, getProjectTeamUsers } from '../utils/projectAccess';
import { useOrganizationData } from '../hooks/useOrganizationData';

const ITEM_TYPE = 'TASK';

interface DraggableTaskProps {
  task: Task;
  users: any[];
  onClick: () => void;
}

function DraggableTask({ task, users, onClick }: DraggableTaskProps) {
  const [{ isDragging }, drag] = useDrag({
    type: ITEM_TYPE,
    item: { taskId: task.id },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  return (
    <div
      ref={drag as unknown as Ref<HTMLDivElement>}
      className={isDragging ? 'opacity-50' : 'opacity-100'}
    >
      <TaskCard task={task} users={users} onClick={onClick} />
    </div>
  );
}

interface ColumnProps {
  status: TaskStatus;
  tasks: Task[];
  users: any[];
  onTaskClick: (task: Task) => void;
  onDrop: (taskId: string, newStatus: TaskStatus) => void;
}

function Column({ status, tasks, users, onTaskClick, onDrop }: ColumnProps) {
  const [{ isOver }, drop] = useDrop({
    accept: ITEM_TYPE,
    drop: (item: { taskId: string }) => {
      onDrop(item.taskId, status);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  const statusColors: Record<TaskStatus, string> = {
    'Active': 'bg-primary/10 border-primary/40',
    'Not Started': 'bg-muted/40 border-border',
    'In Progress': 'bg-chart-4/10 border-chart-4/40',
    'Code Complete': 'bg-chart-4/10 border-chart-4/40',
    'In Release': 'bg-chart-1/10 border-chart-1/40',
    'Paused': 'bg-chart-5/10 border-chart-5/40',
    'Completed': 'bg-chart-2/10 border-chart-2/40',
    'Blocked': 'bg-destructive/10 border-destructive/40',
    'Deferred': 'bg-chart-5/10 border-chart-5/40',
    'Not Required': 'bg-muted/40 border-border',
    'Duplicate': 'bg-muted/40 border-border',
    'Other': 'bg-muted/40 border-border',
  };

  return (
    <div
      ref={drop as unknown as Ref<HTMLDivElement>}
      className={`flex-1 min-w-[300px] bg-white rounded-lg border-2 transition-colors ${
        isOver ? 'border-primary/40 bg-primary/10' : statusColors[status]
      }`}
    >
      <div className="p-3 border-b border-border bg-white rounded-t-lg sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold tracking-tight">{status}</h3>
          <span className="px-2 py-0.5 bg-muted/60 rounded-full text-xs font-medium text-muted-foreground">
            {tasks.length}
          </span>
        </div>
      </div>
      <div className="pretty-scrollbar flex flex-col gap-3 p-3 max-h-[calc(100vh-22rem)] min-h-[200px] overflow-y-auto">
        {tasks.map((task) => (
          <DraggableTask key={task.id} task={task} users={users} onClick={() => onTaskClick(task)} />
        ))}
      </div>
    </div>
  );
}

export default function SprintBoard() {
  const { projectId, sprintId } = useParams();
  const { state, loading, currentDate, reload } = useApp();
  const { projects, tasks, sprints, users, epics } = useOrganizationData(state) as {
    projects: Project[];
    tasks: Task[];
    sprints: any[];
    users: any[];
    epics: any[];
  };
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  if (loading || !state) {
    return <div className="p-8 text-center">Loading sprint...</div>;
  }

  if (!state.currentUser) {
    return <div className="p-8 text-center">Loading user context...</div>;
  }

  const sprint = sprints.find((s) => s.id === sprintId);
  const project = projects.find((p) => p.id === projectId);
  const projectUsers = project ? getProjectTeamUsers(project, users, state?.teams || []) : [];
  const sprintTasks = tasks.filter((t) => (sprint?.taskIds || []).includes(t.id));
  const scopedProjectIds = new Set(projects.map((item) => item.id));
  const scopedStories = (state?.stories || []).filter((story) => scopedProjectIds.has(story.projectId));

  const refreshState = async () => {
    const email = state.currentUser?.email;
    if (email) {
      await reload(email);
    }
  };

  const currentUserId = state.currentUser.id;
  const currentUserName = state.currentUser.name;

  const handleTaskDrop = async (taskId: string, newStatus: TaskStatus) => {
    if (!canEditTaskParameters(state.currentUser, project || null)) {
      return;
    }

    await updateTask(taskId, { status: newStatus });
    await refreshState();
  };

  const handleCreateTask = async (task: Task) => {
    // See docs/design/WORK_ITEM_LINKING_DESIGN.md §5.1 — "Create and link" pattern.
    if (!canCreateProjectTasks(state.currentUser, project || null)) {
      toast.error('You do not have permission to create tasks in this project.');
      return;
    }

    // Ensure the task has an id before saving so we can link it to the sprint
    const taskId = task.id || generateId('task', task.projectId);
    const taskToSave: Task = { ...task, id: taskId };

    try {
      await addTask(taskToSave);

      // Link the new task to the current sprint
      if (sprint && !sprint.taskIds?.includes(taskId)) {
        const updatedTaskIds = [...(sprint.taskIds || []), taskId];
        await updateSprint(sprint.id, { taskIds: updatedTaskIds });
      }

      setIsCreateModalOpen(false);
      await refreshState();
      toast.success('Task added to sprint');
    } catch (error) {
      console.error('[SprintBoard] Failed to create task:', error);
      toast.error('Failed to create task. Please try again.');
    }
  };

  const handleUpdateTask = async (taskId: string, updates: Partial<Task>) => {
    const updatedAt = new Date().toISOString();
    await updateTask(taskId, { ...updates, updatedAt });
    await refreshState();

    if (selectedTask?.id === taskId) {
      setSelectedTask({ ...selectedTask, ...updates, updatedAt });
    }
  };

  const columns: TaskStatus[] = ['Active', 'In Progress', 'Code Complete', 'In Release', 'Blocked', 'Completed'];

  if (!sprint || !project) {
    return (
      <div className="p-6 lg:p-8 max-w-7xl mx-auto">
        <div className="text-center py-12">
          <p className="text-muted-foreground mb-4">Sprint not found</p>
          <Link to="/app/projects" className="text-primary hover:text-primary">
            Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  const startDate = new Date(sprint.startDate);
  const endDate = new Date(sprint.endDate);
  const today = currentDate;
  const daysRemaining = Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  const canCreateTasks = canCreateProjectTasks(state.currentUser, project);

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="p-6 lg:p-8 max-w-[1800px] mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Link
            to={`/app/projects/${projectId}/sprints`}
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-muted-foreground mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Sprint Management
          </Link>

          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-4">
            <div>
              <h1 className="text-3xl mb-2">{sprint.name}</h1>
              <p className="text-muted-foreground">{project.name}</p>
            </div>
            {canCreateTasks && (
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary/10 text-white rounded-lg hover:bg-primary/10"
              >
                <Plus className="w-5 h-5" />
                Add Task
              </button>
            )}
          </div>

          {/* Sprint Info */}
          <div className="flex flex-wrap items-center gap-6 text-sm">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Calendar className="w-4 h-4" />
              <span>{startDate.toLocaleDateString()} - {endDate.toLocaleDateString()}</span>
            </div>
            {sprint.status === 'Active' && (
              <span className={daysRemaining > 0 ? 'text-primary' : 'text-destructive'}>
                {daysRemaining > 0 ? `${daysRemaining} days remaining` : 'Sprint overdue'}
              </span>
            )}
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">{sprint.goals}</span>
            </div>
          </div>
        </div>

        {/* Board */}
        <div className="pretty-scrollbar flex gap-4 overflow-x-auto pb-2 mb-6">
          {columns.map((status) => (
            <Column
              key={status}
              status={status}
              tasks={sprintTasks.filter((t) => t.status === status)}
              users={projectUsers}
              onTaskClick={setSelectedTask}
              onDrop={handleTaskDrop}
            />
          ))}
        </div>

        {/* Comment Section */}
        <div className="bg-white rounded-lg p-6 shadow-sm border border-border mb-6">
          <CommentSection
            comments={sprint.comments || []}
            currentUserId={currentUserId}
            currentUserName={currentUserName}
            onAddComment={async (content, parentCommentId, _includeInStandup, _includeInProjectFeed, _includeInOrgFeed, attachments) => {
              const newComment: Comment = {
                id: generateId('comment', sprint.id),
                content,
                createdBy: currentUserId,
                createdByName: currentUserName,
                createdAt: new Date().toISOString(),
                parentCommentId,
                attachments,
              };
              await addCommentToEntity('sprint', sprint.id, newComment);
              await refreshState();
              toast.success('Comment added');
            }}
            onEditComment={async (commentId, newContent, attachments) => {
              const updates: Partial<Comment> = { content: newContent };
              if (attachments) {
                updates.attachments = attachments;
              }

              await updateCommentInEntity('sprint', sprint.id, commentId, updates);
              await refreshState();
              toast.success('Comment updated');
            }}
            onDeleteComment={async (commentId) => {
              await deleteCommentFromEntity('sprint', sprint.id, commentId);
              await refreshState();
              toast.success('Comment deleted');
            }}
            onAddReaction={async (commentId, emoji) => {
              await addReactionToComment('sprint', sprint.id, commentId, currentUserId, emoji);
              await refreshState();
            }}
            placeholder="Share your thoughts about this sprint..."
          />
        </div>

        {selectedTask && (
          <TaskDetailsModal
            task={selectedTask}
            onClose={() => setSelectedTask(null)}
            onUpdate={handleUpdateTask}
            users={users}
            currentUser={state.currentUser}
            existingTasks={tasks.filter(t => t.projectId === projectId)}
            sprints={sprints.filter(s => s.projectId === projectId)}
            teams={state.teams || []}
            project={project as Project}
          />
        )}

        {isCreateModalOpen && (
          <CreateTaskModal
            projectId={projectId!}
            onClose={() => setIsCreateModalOpen(false)}
            onCreate={handleCreateTask}
            users={projectUsers}
            teamMemberIds={Array.from(getProjectTeamMemberIds(project, state?.teams || []))}
            existingTasks={tasks.filter(t => t.projectId === projectId)}
            epics={epics.filter(e => e.projectId === projectId)}
            stories={scopedStories.filter(s => s.projectId === projectId)}
            project={project}
            orgId={project?.organizationId || state.currentOrganization?.id || state.currentUser.organizationId}
          />
        )}
      </div>
    </DndProvider>
  );
}